import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listProducts, listBaseUnits } from "../services/product";

const PAGE_SIZE = 8;

// Split out of the former ProdutosLayer. Mirrors
// sge_infantil_web/src/pages/expenses/products/list/index.js: the create
// screen is hidden from the menu and only reached through the "Adicionar
// Produto" button here (see CadastrarProdutoLayer / App.js route
// `/despesas/produtos/cadastrar`).
const ListaProdutosLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [baseUnits, setBaseUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const fetchProducts = () => {
    setLoading(true);
    listProducts()
      .then((resp) => setProducts(resp.data || []))
      .catch(() => toast.error(t("produto.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    listBaseUnits().then((resp) => setBaseUnits(resp.data || [])).catch(() => setBaseUnits([]));
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unitName = (id) => baseUnits.find((u) => u.id === id)?.name || "-";

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return products;
    return products.filter((p) => p.name?.toLowerCase().includes(term));
  }, [products, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (next) => {
    if (next < 1 || next > totalPages) return;
    setPage(next);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("produto.section.list")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("produto.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-primary-600 d-flex align-items-center gap-2'
                onClick={() => navigate("/despesas/produtos/cadastrar")}
              >
                <Icon icon='ic:baseline-plus' />
                {t("produto.list.add")}
              </button>
            </div>

            <div className='mb-16'>
              <div className='icon-field max-w-320-px'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='ion:search-outline' />
                </span>
                <input
                  type='text'
                  className='form-control'
                  placeholder={t("produto.list.search.placeholder")}
                  value={search}
                  onChange={handleSearch}
                />
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col'>{t("produto.field.name")}</th>
                    <th scope='col'>{t("produto.field.baseUnit")}</th>
                    <th scope='col'>{t("produto.field.unitPrice")}</th>
                    <th scope='col'>{t("produto.field.packagePrice")}</th>
                    <th scope='col'>{t("produto.col.available")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={5} className='text-center text-secondary-light'>
                        {t("global.loading")}
                      </td>
                    </tr>
                  )}
                  {!loading && pageItems.length === 0 && (
                    <tr>
                      <td colSpan={5} className='text-center text-secondary-light'>
                        {t("produto.hint.noProducts")}
                      </td>
                    </tr>
                  )}
                  {pageItems.map((p) => (
                    <tr key={p.id}>
                      <td className='fw-semibold text-primary-light'>{p.name}</td>
                      <td>{p.baseUnit?.name || unitName(p.baseUnitId)}</td>
                      <td>{p.unitPrice || 0}</td>
                      <td>{p.packagePrice || 0}</td>
                      <td>{p.availableQuantity || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-16'>
              <span className='text-secondary-light text-sm'>
                {filtered.length} {t("produto.list.total")}
              </span>
              {!loading && filtered.length > PAGE_SIZE && (
                <ul className='pagination d-flex flex-wrap align-items-center gap-2 mb-0'>
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                    >
                      <Icon icon='ep:d-arrow-left' className='text-xl' />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className='page-item'>
                      <button
                        type='button'
                        className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px ${
                          p === page ? "bg-primary-600 text-white" : "bg-primary-50 text-secondary-light"
                        }`}
                        onClick={() => goToPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      <Icon icon='ep:d-arrow-right' className='text-xl' />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListaProdutosLayer;
